import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { motion } from "framer-motion";
import { LOGO } from "./assets";
import { useLang } from "./LanguageContext";
import LanguageSwitcher from "./LanguageSwitcher";
import Footer from "./Footer";

export const LegalPage = ({ docKey }) => {
  const { t } = useLang();
  const sections = t(`legal.${docKey}.sections`) || [];

  return (
    <div data-testid={`legal-page-${docKey}`} className="min-h-screen bg-[#060A14] text-white selection:bg-[#00F2FE]/30">
      {/* Top bar */}
      <header className="sticky top-0 z-50 glass border-b border-[#00F2FE]/15">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
          <Link to="/" data-testid="legal-logo" className="flex items-center flicker">
            <img src={LOGO} alt="Ino Elites" className="h-9 md:h-11 w-auto drop-shadow-[0_0_14px_rgba(0,242,254,0.55)]" />
          </Link>
          <div className="flex items-center gap-4 md:gap-6">
            <LanguageSwitcher />
            <Link
              to="/"
              data-testid="legal-back-button"
              className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-300 hover:text-[#00F2FE] transition-colors"
            >
              <ArrowLeft size={16} /> {t("legal.back")}
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="relative overflow-hidden">
        {/* Decorative kanji watermark */}
        <span className="pointer-events-none select-none absolute -top-10 right-0 text-[28vw] leading-none font-display font-black text-white/[0.02]">
          {docKey === "privacy" ? "秘" : "約"}
        </span>

        <div className="relative max-w-3xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-20">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <p className="font-mono-x text-[11px] font-bold tracking-[0.35em] text-[#F9D423] mb-4">
              {t("legal.lastUpdated")}
            </p>
            <h1 className="font-display font-black uppercase text-4xl sm:text-5xl lg:text-6xl leading-none mb-6 drop-shadow-[0_0_18px_rgba(0,242,254,0.25)]">
              {t(`legal.${docKey}.title`)}
            </h1>
            <div className="h-px w-24 bg-gradient-to-r from-[#00F2FE] to-transparent mb-8" />
            <p className="text-base text-slate-300 leading-relaxed mb-14">
              {t(`legal.${docKey}.intro`)}
            </p>
          </motion.div>

          <div className="space-y-12">
            {sections.map((s, i) => (
              <motion.section
                key={i}
                data-testid={`legal-section-${i}`}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: 0.04 * i }}
              >
                <h2 className="flex items-baseline gap-3 text-lg font-bold text-white mb-3">
                  <span className="font-mono-x text-sm text-[#00F2FE]">{String(i + 1).padStart(2, "0")}</span>
                  {s.h}
                </h2>
                {s.p.map((para, j) => (
                  <p key={j} className="text-sm md:text-base text-slate-400 leading-relaxed mb-3">
                    {para}
                  </p>
                ))}
              </motion.section>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default LegalPage;
